import Utils from '@/core/utils/Utils';
import EntityPointer from "@/domains/EntityPointer/EntityPointer";
import FileDescriptor from "@/core/services/Files/FileDescriptor";
import DateTimeRange from "@/core/types/DateTimeRange";
import TimeStamp from "@/core/types/TimeStamp";
import type { UnwrapNestedRefs } from 'vue';

/**
 * Операция с файлом (загрузка/скачивание)
 * Представляет состояние и прогресс файловой операции
 * Инкапсулирует всю логику управления статусом и прогрессом
 */
class FileOperation {

    id: string;
    type: 'upload' | 'download';
    fileName: string;
    status: 'pending' | 'progress' | 'completed' | 'error' | 'cancelled';
    progress: number;
    time: DateTimeRange;
    error?: string;
    entityName?: string;
    fileSize?: number;
    bytesTransferred?: number;
    abortController: AbortController;

    constructor() {
        this.abortController = new AbortController();
        this.status = 'pending';
        this.time = new DateTimeRange(new TimeStamp(), null);
        this.bytesTransferred = 0;
        this.progress = 0;
    }

    /**
     * Создать операцию загрузки файла
     */
    static fromUploadRequest(file: File, entity: EntityPointer): UnwrapNestedRefs<FileOperation> {
        const operation = reactive(new FileOperation());
        operation.id = `upload_${file.name}`;
        operation.id =  this.getUploadIdByFile(file);

        operation.type = 'upload';
        operation.fileName = file.name;
        operation.fileSize = file.size;
        operation.entityName = entity.name;

        return operation;
    }

    /**
     * Создать операцию скачивания файла
     */
    static fromDownloadRequest(fileDescriptor: FileDescriptor): UnwrapNestedRefs<FileOperation> {
        const operation = reactive(new FileOperation());
        operation.id = this.getDownloadIdByFileDescriptor(fileDescriptor);

        operation.type = 'download';
        operation.fileName = fileDescriptor.name;
        operation.fileSize = fileDescriptor.size;
        operation.entityName = fileDescriptor.parentEntity?.name;

        return operation;
    }

    /**
     * Обновить прогресс операции
     */
    updateProgress(progress: number, bytesTransferred?: number): void {
        this.progress = Math.max(0, Math.min(100, progress));
        this.status = 'progress';

        if (bytesTransferred !== undefined) {
            this.bytesTransferred = bytesTransferred;
        }
    }

    /**
     * Обновить комбинированный прогресс (конвертация + загрузка)
     * Инкапсулирует логику расчета прогресса для многоэтапных операций
     */
    updateCombinedProgress(
        conversionPercent: number,
        uploadProgress: { percentage: number; loaded?: number }
    ): void {
        if (!this.fileSize) return;

        const totalProgress = conversionPercent + Math.round(uploadProgress.percentage * (100 - conversionPercent) / 100);
        const conversionBytes = Math.floor(this.fileSize * conversionPercent / 100);
        const bytesTransferred = conversionBytes + (uploadProgress.loaded || 0);

        this.updateProgress(totalProgress, bytesTransferred);
    }

    /**
     * Завершить операцию успешно
     */
    complete(): void {
        this.status = 'completed';
        this.progress = 100;
        this.bytesTransferred = this.fileSize;
        this.time.end = new TimeStamp();
    }

    /**
     * Завершить операцию с ошибкой
     */
    fail(errorMessage: string): void {
        this.status = 'error';
        this.error = errorMessage;
        this.time.end = new TimeStamp();
    }

    /**
     * Отменить операцию. Работает только для активных операций.
     */
    cancel(): void {
        if (!this.canBeCancelled()) return;

        this.status = 'cancelled';
        this.abortController.abort(); // Отменяем HTTP запрос
        this.time.end = new TimeStamp();
    }

    /**
     * Может ли операция быть отменена
     */
    canBeCancelled(): boolean {
        return ['pending', 'progress'].includes(this.status);
    }

    /**
     * Активна ли операция в данный момент
     */
    get isActive(): boolean {
        return ['pending', 'progress'].includes(this.status);
    }

    /**
     * Завершена ли операция (любым способом)
     */
    get isCompleted(): boolean {
        return ['completed', 'error', 'cancelled'].includes(this.status);
    }

    get isSuccessfullyCompleted() {
        return this.status === 'completed';
    }

    get isError() {
        return this.status === 'error';
    }

    get isCancelled() {
        return this.status === 'cancelled';
    }

    /**
     * Форматированный размер файла
     */
    get formattedSize(): string {
        return Utils.file.formatSize(this.fileSize || 0);
    }

    /**
     * Форматированный прогресс: "45% (2.1 МБ из 4.7 МБ)"
     */
    get formattedProgress(): string {
        if (!this.fileSize) {
            return `${this.progress}%`;
        }

        const totalSize = this.formattedSize;
        const transferredSize = Utils.file.formatSize(this.bytesTransferred || 0);

        return `${this.progress}% (${transferredSize} из ${totalSize})`;
    }

    /**
     * Приблизительное время до завершения на основе текущей скорости передачи
     */
    get remainingTime(): number | null {
        if (!this.isActive || this.progress === 0 || !this.fileSize || !this.time.start) {
            return null;
        }

        const elapsedTime = (Date.now() - this.time.start.getTime()) / 1000;

        // Защита от деления на ноль
        if (elapsedTime <= 0) return null;

        const speed = (this.bytesTransferred || 0) / elapsedTime;
        const remainingBytes = this.fileSize - (this.bytesTransferred || 0);

        return speed > 0 ? Math.ceil(remainingBytes / speed) : null;
    }

    /**
     * Форматированное время до завершения
     */
    get formattedRemainingTime(): string | null {
        const seconds = this.remainingTime;
        if (!seconds) return null;

        if (seconds < 60) {
            return `${seconds} сек`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return remainingSeconds > 0 ? `${minutes} мин ${remainingSeconds} сек` : `${minutes} мин`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours} ч ${minutes} мин`;
        }
    }

    /**
     * Иконка типа операции
     */
    get operationIcon(): string {
        return this.type === 'upload' ? 'upload' : 'download';
    }

    /**
     * Иконка статуса операции
     */
    get statusIcon(): string {
        switch (this.status) {
            case 'pending':
                return 'hourglass_empty';
            case 'progress':
                return 'sync';
            case 'completed':
                return 'check_circle';
            case 'error':
                return 'error';
            case 'cancelled':
                return 'cancel';
            default:
                return 'help';
        }
    }

    /**
     * Сигнал для отмены HTTP запросов
     */
    get abortSignal(): AbortSignal {
        return this.abortController.signal;
    }

    /** Метод занимающийся формирование айдишиника для операции */
    static getDownloadIdByFileDescriptor(fileDescriptor: FileDescriptor): string {
        return `download_${fileDescriptor.uid}`;
    }

    /** Метод занимающийся формирование айдишиника для операции */
    static getUploadIdByFile(fileDescriptor: File):string {
        return `upload_${fileDescriptor.name}`;
    }
}

export default FileOperation;
